<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { YardTaskStatus, getCategoryLabelTranslationKey, YardTaskCheckEntity } from '@nova/core';
import { useYardTaskMutations } from '@satellite/composables/useYardTaskMutations';
import { useYardTaskPresenter } from '@satellite/composables/useYardTaskPresenter';
import { useNotify } from '@/composables';
import { KeyValueList, KeyValue } from '@satellite/components/elements/key_value';

import YardAreaPresenter from '@satellite/components/elements/presenters/yard/YardAreaPresenter.vue';
import YardAssetPresenter from '@satellite/components/elements/presenters/yard/YardAssetPresenter.vue';
import YardSpotPresenter from '@satellite/components/elements/presenters/yard/YardSpotPresenter.vue';

import type { TaskViewData } from '@nova/core';

const props = defineProps<{
  modelValue: boolean;
  task: TaskViewData | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  cancelled?: [task: TaskViewData];
}>();

const { t } = useI18n();
const notify = useNotify();

const isCancelling = ref(false);

const presenter = useYardTaskPresenter(
  computed(() => props.task),
  { i18n: t }
);

const taskNumber = computed(() => presenter.taskNumber.value);
const isMoveTask = computed(() => presenter.isMoveTask.value);
const asset = computed(() => presenter.getAssetContainer());
const originSpotId = computed(() => presenter.getOriginSpotId());
const destinationSpotId = computed(() => presenter.getDestinationSpotId());
const taskCategory = computed(() => presenter.getTaskCategory());

const subject = computed(() => presenter.getRelatedEntity());
const subjectId = computed(() => presenter.getRelatedId());
const subjectObject = computed(() => presenter.getRelatedObject());

const taskCategoryLabel = computed(() => {
  const category = taskCategory.value;
  if (!category) {
    return null;
  }
  const key = getCategoryLabelTranslationKey(category);
  return key ? t(key) : category;
});

const { updateTask: updateTaskMutation } = useYardTaskMutations();

const updateModelValue = (value: boolean) => {
  emit('update:modelValue', value);
};

const handleNevermind = () => {
  updateModelValue(false);
};

const handleConfirmCancel = async () => {
  if (!props.task?.id) {
    console.warn('[YardTaskCancelConfirmationDialog] Cannot cancel task - task ID is missing');
    return;
  }

  try {
    isCancelling.value = true;
    const cancelledTask = await updateTaskMutation(props.task.id, {
      status: YardTaskStatus.CANCELLED
    });

    notify(t('yard.messages.taskCancelledSuccessfully'), 'success');
    updateModelValue(false);
    emit('cancelled', cancelledTask);
  } catch (error) {
    console.error('[YardTaskCancelConfirmationDialog] Failed to cancel task:', error);
    notify(t('yard.messages.failedToCancelTask'), 'error');
  } finally {
    isCancelling.value = false;
  }
};
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="updateModelValue"
    max-width="600"
    persistent
    @keydown.escape="handleNevermind"
    data-testid="yard-task-cancel-confirmation-dialog">
    <v-card>
      <v-card-title class="d-flex flex-row align-center gap-2 pa-4">
        <v-icon icon="mdi-close-circle" color="error" size="20" />
        <span class="typography-heading-lg">{{ t('yard.dialogs.cancelTask.title') }}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          @click="handleNevermind"
          data-testid="yard-task-cancel-dialog-close-button" />
      </v-card-title>

      <v-card-text class="d-flex flex-column gap-4 pa-4">
        <i18n-t
          :keypath="
            isMoveTask
              ? 'yard.dialogs.cancelTask.message.move'
              : 'yard.dialogs.cancelTask.message.check'
          "
          tag="p"
          class="typography-body-sm">
          <template #taskNumber>
            <span class="typography-body-sm-bold">{{ taskNumber || '--' }}</span>
          </template>
        </i18n-t>

        <v-card
          variant="outlined"
          class="pa-3 border--color-line-border bg--color-neutral-10"
          data-testid="yard-task-cancel-dialog-task-info">
          <template v-if="isMoveTask">
            <div class="d-flex flex-column gap-3">
              <div class="d-flex flex-row gap-1 align-center">
                <yard-asset-presenter
                  v-if="asset"
                  :asset="asset"
                  size="small"
                  variant="asset-only" />
                <div v-else class="typography-body-sm-bold">
                  {{ t('yard.pages.yardTasks.labels.anyAsset') }}
                </div>
              </div>

              <key-value-list class="gap-2">
                <key-value>
                  <template #label>
                    <span class="typography-body-sm-bold">
                      {{ t('yard.pages.yardTasks.labels.from') }}:
                    </span>
                  </template>
                  <template #value>
                    <yard-spot-presenter
                      v-if="originSpotId"
                      :spot-id="originSpotId"
                      size="small"
                      class="flex-grow-1" />
                    <span v-else class="typography-body-sm text--color-text-placeholder">
                      {{ t('yard.pages.yardTasks.labels.fromAnySpot') }}
                    </span>
                  </template>
                </key-value>
                <key-value>
                  <template #label>
                    <span class="typography-body-sm-bold">
                      {{ t('yard.pages.yardTasks.labels.to') }}:
                    </span>
                  </template>
                  <template #value>
                    <yard-spot-presenter
                      v-if="destinationSpotId"
                      :spot-id="destinationSpotId"
                      size="small"
                      class="flex-grow-1" />
                    <span v-else class="typography-body-sm text--color-text-placeholder">
                      {{ t('yard.pages.yardTasks.labels.toAnySpot') }}
                    </span>
                  </template>
                </key-value>
              </key-value-list>
            </div>
          </template>

          <template v-else>
            <div class="d-flex flex-column gap-2">
              <div class="typography-heading-sm-bold">
                {{ taskCategoryLabel }}
              </div>
              <div v-if="subject === YardTaskCheckEntity.SpotArea" class="d-flex flex-column gap-1">
                <span class="d-flex flex-row gap-1 align-center">
                  <v-icon icon="mdi-map-marker-outline" size="16" />

                  <yard-area-presenter
                    :area-id="subjectId"
                    :area="subjectObject"
                    size="small"
                    class="flex-grow-1" />
                </span>
              </div>
              <div
                v-else-if="subject === YardTaskCheckEntity.Spot"
                class="d-flex flex-column gap-1">
                <span class="d-flex flex-row gap-1 align-center">
                  <v-icon icon="mdi-map-marker-outline" size="16" />
                  <yard-spot-presenter
                    :spot-id="subjectId"
                    :spot="subjectObject"
                    size="small"
                    class="flex-grow-1" />
                </span>
              </div>
              <div
                v-else-if="subject === YardTaskCheckEntity.AssetContainer"
                class="d-flex flex-column gap-1">
                <yard-asset-presenter
                  :asset-id="subjectId"
                  :asset="subjectObject"
                  variant="asset-only"
                  size="small"
                  class="flex-grow-1" />
              </div>
            </div>
          </template>
        </v-card>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="d-flex flex-row gap-2 pa-4">
        <v-btn
          variant="outlined"
          @click="handleNevermind"
          :disabled="isCancelling"
          data-testid="yard-task-cancel-dialog-nevermind-button">
          {{ t('global.buttons.nevermind') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          @click="handleConfirmCancel"
          :disabled="isCancelling"
          :loading="isCancelling"
          data-testid="yard-task-cancel-dialog-confirm-button">
          {{ t('yard.dialogs.cancelTask.buttons.yesCancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
